import { parseSyncMessage, type SyncMessage } from '@qcdoc/mock-runtime/sync/protocol.ts';
import type { SyncReplica } from '@qcdoc/mock-runtime/sync/replica.ts';

const HEARTBEAT_MS = 3000;
/** A visible window that stays silent for this long is treated as gone; hidden tabs throttle their timers. */
const SILENCE_MS = 15000;

export interface PeerLinkOptions {
  portal: 'internal' | 'vendor';
  peerPortal: 'internal' | 'vendor';
  /** The only origin whose messages are accepted and the only target messages are posted to. */
  peerOrigin: string;
  replica: SyncReplica;
  /** Called whenever connectivity or the number of unacknowledged changes may have changed. */
  onChange(): void;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const isClosed = (target: Window): boolean => {
  try { return target.closed; } catch { return true; }
};

/**
 * Exchanges synchronization messages with windows of the other portal through window.postMessage.
 * Browsers expose no way to find windows of another origin, so windows are connected through the
 * opener relationship: the portal that opened the other one, or the one that was opened by it.
 */
export class PeerLink {
  private readonly connected = new Map<Window, number>();
  private readonly candidates = new Set<Window>();
  private timer: number | undefined;
  private readonly options: PeerLinkOptions;

  constructor(options: PeerLinkOptions) {
    this.options = options;
  }

  get isConnected(): boolean {
    return this.connected.size > 0;
  }

  start(): void {
    window.addEventListener('message', this.onMessage);
    window.addEventListener('pagehide', this.onPageHide);
    const opener = window.opener as Window | null;
    if (opener && opener !== window && !isClosed(opener)) this.candidates.add(opener);
    this.timer = window.setInterval(this.heartbeat, HEARTBEAT_MS);
    this.heartbeat();
  }

  stop(): void {
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('pagehide', this.onPageHide);
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.timer = undefined;
    this.connected.clear();
    this.candidates.clear();
  }

  /** Opens (or re-uses) the other portal in a named window that keeps its opener link to this one. */
  open(path: string): void {
    if (this.isConnected) {
      for (const target of this.connected.keys()) {
        try { target.focus(); } catch { continue; }
      }
      return;
    }
    const target = window.open(`${this.options.peerOrigin}/#${path}`, `qcdoc-${this.options.peerPortal}-portal`);
    if (!target) return;
    this.candidates.add(target);
    for (const delay of [600, 1500, 3000]) window.setTimeout(this.heartbeat, delay);
  }

  broadcast(messages: SyncMessage[]): void {
    for (const target of this.connected.keys()) this.send(target, messages);
  }

  /** Re-sends everything the other portal has not acknowledged. */
  flushPending(): void {
    if (!this.isConnected || !this.options.replica.pendingCount()) return;
    void this.options.replica.pendingChanges().then((messages) => { this.broadcast(messages); }).catch(report);
  }

  private post(target: Window, payload: Record<string, unknown>): void {
    try {
      target.postMessage({ qcdocSync: 1, portal: this.options.portal, ...payload }, this.options.peerOrigin);
    } catch {
      // A window that disappeared or navigated away is pruned by the heartbeat.
    }
  }

  private send(target: Window, messages: SyncMessage[]): void {
    for (const message of messages) this.post(target, { message });
  }

  private readonly heartbeat = (): void => {
    const now = Date.now();
    for (const target of [...this.connected.keys()]) {
      const silent = !document.hidden && now - (this.connected.get(target) ?? 0) > SILENCE_MS;
      if (isClosed(target)) this.connected.delete(target);
      else if (silent) { this.connected.delete(target); this.candidates.add(target); }
    }
    for (const target of [...this.candidates]) if (isClosed(target)) this.candidates.delete(target);
    for (const target of [...this.connected.keys(), ...this.candidates]) this.post(target, { control: 'ping' });
    this.flushPending();
    this.options.onChange();
  };

  private readonly onPageHide = (): void => {
    for (const target of this.connected.keys()) this.post(target, { control: 'bye' });
  };

  private readonly onMessage = (event: MessageEvent): void => {
    const { peerOrigin, peerPortal, replica } = this.options;
    const data: unknown = event.data;
    if (event.origin !== peerOrigin || !event.source || !isObject(data) || data.qcdocSync !== 1 || data.portal !== peerPortal) return;
    const source = event.source as Window;
    const wasConnected = this.connected.has(source);
    this.connected.set(source, Date.now());
    this.candidates.delete(source);
    if (data.control === 'bye') {
      this.connected.delete(source);
      this.candidates.add(source);
      this.options.onChange();
      return;
    }
    if (!wasConnected) {
      this.send(source, [replica.hello(true)]);
      this.options.onChange();
    }
    if (data.control === 'ping') this.post(source, { control: 'pong' });
    if (data.control) return;
    const message = parseSyncMessage(data.message);
    if (!message) return;
    void replica.receive(message).then((replies) => { this.send(source, replies); }).catch(report);
  };
}

function report(error: unknown): void {
  console.error('Mock data synchronization failed.', error);
}
